import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Film, Clock, List, Monitor, Save, ArrowLeft, Plus, Loader2, Wand2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useEditorStore } from '../../stores/editorStore';
import { useProjectStore } from '../../stores/projectStore';
import { useMusicStore } from '../../stores/musicStore';
import { VideoPlayer, ShotNavigator, ShotEditor } from '../../components/organisms';
import { Button } from '../../components/atoms/Button';
import { Input } from '../../components/atoms/Input';
import { useProject } from '../../hooks/useProjects';
import { useGenerateVideo } from '../../hooks/useGenerateVideo';
import type { SceneWithShots, Shot } from '../../types/database';

export function EditorPage() {
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const { session } = useAuthStore();
  const { viewMode, setViewMode } = useEditorStore();
  const { scenes, setScenes, getTotalDuration, currentProject, setCurrentProject, setProjectConfig } = useProjectStore();
  const { setTracks, setCurrentTrack, tracks: musicTracks } = useMusicStore();
  const generateVideo = useGenerateVideo();
  
  // Fetch project data if projectId is provided
  const { data: projectData, isLoading } = useProject(projectId || null);

  // Set current project ID in store
  useEffect(() => {
    if (projectData?.project) {
      setCurrentProject(projectData.project);
      setProjectConfig(projectData.config);
    }
  }, [projectData, setCurrentProject, setProjectConfig]);

  // Load music tracks into music store
  useEffect(() => {
    if (projectData?.musicTracks && projectData.musicTracks.length > 0) {
      setTracks(projectData.musicTracks);
      const activeTrack = projectData.musicTracks.find(t => t.is_active) || projectData.musicTracks[0];
      setCurrentTrack(activeTrack);
    }
  }, [projectData?.musicTracks, setTracks, setCurrentTrack]);

  // Load project data - only on first load or when switching projects
  // Avoids stale React Query cache overwriting locally-edited zustand data
  useEffect(() => {
    if (projectData?.scenes) {
      const currentScenes = useProjectStore.getState().scenes;
      const alreadyLoaded =
        currentScenes.length > 0 &&
        currentScenes[0]?.project_id === projectId;
      if (!alreadyLoaded) {
        setScenes(projectData.scenes);
      }
    }
  }, [projectData, projectId, setScenes]);

  const totalTime = useMemo(() => getTotalDuration(), [getTotalDuration]);

  const handleExport = () => {
    const dataStr =
      'data:text/json;charset=utf-8,' +
      encodeURIComponent(JSON.stringify(scenes, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute('href', dataStr);
    downloadAnchorNode.setAttribute(
      'download',
      `zadar_production_${Date.now()}.json`
    );
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleBackToProjects = () => {
    navigate('/projects');
  };

  const handleRegenerateVideo = async () => {
    if (!projectId || !projectData?.config) return;
    
    // Convert config to onboarding data format
    const onboardingData = {
      title: projectData.project.title,
      targetDurationMin: projectData.config.target_duration_min,
      purpose: projectData.config.purpose,
      targetAudience: projectData.config.target_audience || '',
      tone: projectData.config.tone,
      includeNarration: projectData.config.include_narration,
      backgroundMusicMode: projectData.config.background_music_mode || (projectData.config.include_background_music ? 'ai_generated' : 'none'),
      musicGenre: projectData.config.music_genre || 'ambient',
      userCustomPrompt: projectData.config.user_custom_prompt || '',
    };

    try {
      await generateVideo.mutateAsync({
        projectId,
        onboardingData,
      });
      // Refresh the page to load new scenes
      window.location.reload();
    } catch (err) {
      console.error('Failed to regenerate video:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Učitavanje projekta...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] text-slate-200 p-4 md:p-6 font-sans">
      {/* Header */}
      <header className="w-full mb-6 flex justify-between items-center border-b border-white/5 pb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToProjects}
            leftIcon={<ArrowLeft size={16} />}
            className="mr-2"
          >
            Natrag
          </Button>
          <div className="p-2 bg-blue-600 rounded-xl">
            <Film size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tighter uppercase">
              PRODUKCIJSKI <span className="text-blue-500">MASTER</span>
            </h1>
            {projectData?.project && (
              <p className="text-xs text-slate-500 truncate max-w-[200px] md:max-w-md">
                {projectData.project.title}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-[8px] text-slate-500 uppercase font-black">Timeline</p>
            <p className="text-xl font-mono text-amber-500 font-black">
              {Math.floor(totalTime / 60)}:
              {(totalTime % 60).toString().padStart(2, '0')}
            </p>
          </div>
          <div className="flex bg-white/5 p-1 rounded-xl border border-white/10">
            <button
              onClick={() => setViewMode('focused')}
              className={`px-4 py-2 rounded-lg text-[10px] font-bold transition-all ${
                viewMode === 'focused'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-500 hover:text-white'
              }`}
            >
              FOKUS
            </button>
            <button
              onClick={() => setViewMode('expanded')}
              className={`px-4 py-2 rounded-lg text-[10px] font-bold transition-all ${
                viewMode === 'expanded'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-500 hover:text-white'
              }`}
            >
              LISTA
            </button>
          </div>
        </div>
      </header>

      <main className="w-full relative">
        {scenes.length === 0 ? (
          <div className="relative z-10">
            <EmptyProjectState 
              onRegenerate={handleRegenerateVideo}
              isGenerating={generateVideo.isPending}
              projectId={projectId}
            />
          </div>
        ) : viewMode === 'focused' ? (
          <div className="space-y-6">
            {/* Top: Player and Editor */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
              {/* Left: Player */}
              <div className="xl:col-span-7">
                <ShotHeader scenes={scenes} />
                <VideoPlayer scenes={scenes} />
              </div>

              {/* Right: Editor Details */}
              <div className="xl:col-span-5">
                <ShotEditor scenes={scenes} />
              </div>
            </div>

            {/* Bottom: Timeline - Full Width */}
            <div className="w-full">
              <ShotNavigator scenes={scenes} />
            </div>
          </div>
        ) : (
          <ExpandedView scenes={scenes} />
        )}
      </main>

      <footer className="w-full mt-12 pt-6 border-t border-white/5 flex justify-between text-[8px] text-slate-600 font-black uppercase tracking-[0.3em]">
        <p>© 2024 ZADAR ARCHIPELAGO PRODUCTIONS</p>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
          READY FOR MASTERING
        </div>
      </footer>
    </div>
  );
}

// Shot Header - Name and Duration above the video player
function ShotHeader({ scenes }: { scenes: SceneWithShots[] }) {
  const { manualSceneIdx, manualShotIdx } = useEditorStore();
  const { saveShot } = useProjectStore();

  const currentScene = scenes[manualSceneIdx];
  const currentShot = currentScene?.shots?.[manualShotIdx];

  if (!currentShot) return null;

  const handleNameChange = (name: string) => {
    saveShot(currentScene.id, currentShot.id, { name });
  };

  const handleDurationChange = (duration: string) => {
    const durationSec = parseInt(duration) || 5;
    saveShot(currentScene.id, currentShot.id, { duration_sec: durationSec });
  };

  return (
    <div className="flex gap-4 items-center mb-4">
      <div className="flex-1">
        <Input
          label="Naziv Kadra"
          value={currentShot.name}
          onChange={(e) => handleNameChange(e.target.value)}
        />
      </div>
      <div className="w-24 text-center">
        <label className="block text-[11px] font-bold uppercase tracking-widest text-amber-500 mb-1.5">
          Trajanje
        </label>
        <div className="flex items-center justify-center bg-black/40 rounded-xl py-2 px-2 border border-white/5">
          <input
            type="number"
            value={currentShot.duration_sec}
            onChange={(e) => handleDurationChange(e.target.value)}
            className="bg-transparent border-none text-2xl font-black text-amber-500 w-12 text-center focus:ring-0 p-0"
          />
          <span className="text-[10px] text-slate-500 font-bold uppercase">SEC</span>
        </div>
      </div>
    </div>
  );
}

// Empty Project State - when no scenes exist
interface EmptyProjectStateProps {
  onRegenerate: () => void;
  isGenerating: boolean;
  projectId?: string;
}

function EmptyProjectState({ onRegenerate, isGenerating, projectId }: EmptyProjectStateProps) {
  const navigate = useNavigate();
  
  const handleBackToProjects = () => {
    navigate('/projects');
  };

  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-24 h-24 bg-blue-600/10 rounded-full flex items-center justify-center mb-6">
        {isGenerating ? (
          <Loader2 size={48} className="text-blue-500 animate-spin" />
        ) : (
          <Film size={48} className="text-blue-500" />
        )}
      </div>
      
      {isGenerating ? (
        <>
          <h2 className="text-2xl font-bold text-white mb-2">AI generira vaš video...</h2>
          <p className="text-slate-500 mb-8 text-center max-w-md">
            Molimo pričekajte dok AI agenti kreiraju scene, kadrove i vizuale prema vašim uputama.
          </p>
        </>
      ) : (
        <>
          <h2 className="text-2xl font-bold text-white mb-2">Projekt je prazan</h2>
          <p className="text-slate-500 mb-8 text-center max-w-md">
            {projectId 
              ? 'Ovaj projekt još nema generiran sadržaj. Kliknite ispod da AI generira video.'
              : 'Ovaj projekt još nema scena. Vratite se na projekte i kreirajte novi video s AI.'}
          </p>
        </>
      )}
      
      <div className="flex gap-4">
        <Button
          variant="ghost"
          onClick={handleBackToProjects}
          disabled={isGenerating}
        >
          Natrag na Projekte
        </Button>
        
        {projectId && (
          <Button
            variant="primary"
            onClick={onRegenerate}
            isLoading={isGenerating}
            leftIcon={<Wand2 size={18} />}
          >
            {isGenerating ? 'Generiranje...' : 'Generiraj Video'}
          </Button>
        )}
      </div>
    </div>
  );
}

// Expanded List View Component
function ExpandedView({ scenes }: { scenes: SceneWithShots[] }) {
  const { updateShot, removeShot, addShot, addScene, updateScene } = useProjectStore();
  const { goToShot, setViewMode } = useEditorStore();

  const handleShotClick = (sceneIdx: number, shotIdx: number) => {
    goToShot(sceneIdx, shotIdx);
    setViewMode('focused');
  };

  const handleAddScene = () => {
    const newScene: SceneWithShots = {
      id: `scene-${Date.now()}`,
      project_id: 'temp',
      order_index: scenes.length,
      name: `Scena ${scenes.length + 1}`,
      status: 'pending',
      shots: [],
    };
    addScene(newScene);
  };

  return (
    <div className="space-y-12 animate-slide-in">
      {/* Add Scene Button */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleAddScene}
          leftIcon={<Plus size={16} />}
        >
          Dodaj Novu Scenu
        </Button>
      </div>

      {/* Scenes */}
      {scenes.map((scene, sceneIdx) => (
        <div key={scene.id} className="space-y-4">
          {/* Scene Header */}
          <div className="flex items-center gap-4">
            <input
              type="text"
              value={scene.name}
              onChange={(e) => updateScene(scene.id, { name: e.target.value })}
              className="text-lg font-black bg-transparent border-none outline-none text-white uppercase tracking-tight"
            />
            <span className="text-xs text-slate-500">
              {scene.shots.length} kadrova
            </span>
          </div>

          {/* Shots Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {scene.shots.map((shot, shotIdx) => (
              <div
                key={shot.id}
                onClick={() => handleShotClick(sceneIdx, shotIdx)}
                className="group cursor-pointer"
              >
                <div className="aspect-video rounded-xl overflow-hidden border border-white/10 bg-slate-800 relative">
                  {shot.thumbnail_url ? (
                    <img
                      src={shot.thumbnail_url}
                      alt={shot.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film size={24} className="text-slate-600" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-xs font-bold text-white">Uredi</span>
                  </div>
                </div>
                <p className="mt-2 text-xs text-slate-400 truncate">{shot.name}</p>
                <p className="text-[10px] text-slate-600">{shot.duration_sec}s</p>
              </div>
            ))}

            {/* Add Shot Button */}
            <button
              onClick={() =>
                addShot(scene.id, {
                  id: `shot-${Date.now()}`,
                  scene_id: scene.id,
                  order_index: scene.shots.length,
                  name: `Kadar ${scene.shots.length + 1}`,
                  narration_text: '',
                  duration_sec: 5,
                  visual_type: 'image',
                  media_url: null,
                  ai_prompt: '',
                  thumbnail_url: null,
                  text: null,
                  text_enabled: false,
                })
              }
              className="aspect-video rounded-xl border border-dashed border-white/20 flex flex-col items-center justify-center gap-2 hover:bg-white/5 transition-colors"
            >
              <Plus size={20} className="text-slate-500" />
              <span className="text-[10px] text-slate-500">Dodaj Kadar</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
